import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import {
  APPEARANCE_EVENT,
  SCALES,
  applyScale,
  applyTheme,
  readScale,
  readTheme,
} from '../store/theme';
import type { ThemeChoice } from '../store/theme';
import { useStore } from '../store/StoreProvider';
import { waitingSessions } from '../store/selectors';
import { useBreakpoint } from '../lib/useMedia';
import { PopTownMark, PopTownName } from '../components/Brand';
import { VersionModal } from '../components/VersionModal';
import { useUnread } from '../views/notifications/unread';

const SSE_META = {
  connecting: { label: 'Connessione…', color: 'var(--warn)', pulse: true },
  live: { label: 'In diretta', color: 'var(--ok)', pulse: false },
  reconnecting: { label: 'Riconnessione…', color: 'var(--warn)', pulse: true },
  polling: { label: 'Aggiorno ogni 15 s', color: 'var(--warn)', pulse: false },
  down: { label: 'Non connesso', color: 'var(--danger)', pulse: false },
} as const;

// §3.1 — Nell'header NON c'è l'utente: sta in fondo alla sidebar, accanto a Impostazioni
// (§3.2.1). Qui resta solo ciò che cambia da solo e va guardato: le attese e la connessione.
export function Header({ onMenu }: { onMenu: () => void }) {
  const { state, reconnect } = useStore();
  const nav = useNavigate();
  const bp = useBreakpoint();
  const [themeMenu, setThemeMenu] = useState(false);
  const themeBox = useRef<HTMLDivElement>(null);

  // il menu del tema si chiude con Esc e con un clic fuori, come ogni menu
  useEffect(() => {
    if (!themeMenu) return undefined;
    const outside = (e: MouseEvent) => {
      if (!themeBox.current?.contains(e.target as Node)) setThemeMenu(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setThemeMenu(false);
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [themeMenu]);
  const [versionOpen, setVersionOpen] = useState(false);
  const [scale, setScale] = useState(readScale);
  const [choice, setChoice] = useState<ThemeChoice>(() => readTheme());
  const waiting = waitingSessions(state).length;
  const unread = useUnread();
  const sse = SSE_META[state.sse];
  useEffect(() => {
    const sync = () => {
      setChoice(readTheme());
      setScale(readScale());
    };
    window.addEventListener(APPEARANCE_EVENT, sync);
    return () => window.removeEventListener(APPEARANCE_EVENT, sync);
  }, []);

  const setTheme = (c: ThemeChoice) => {
    setChoice(c);
    applyTheme(c);
    setThemeMenu(false);
  };

  return (
    <header
      style={{
        height: bp.xs ? 52 : 56,
        flex: 'none',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: 'color-mix(in srgb, var(--panel) 86%, transparent)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: bp.sm ? '0 20px' : '0 16px',
      }}
    >
      {bp.drawerNav && (
        <Button variant="icon" icon="menu" aria-label="Apri il menu" onClick={onMenu} />
      )}
      <button
        onClick={() => nav('/')}
        style={{
          border: 'none',
          background: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {/* menu aperto: casette e nome; menu chiuso o a cassetto: solo le casette */}
        {bp.collapsed || bp.drawerNav ? (
          <PopTownMark size={28} title="PopTown" />
        ) : (
          <>
            <PopTownMark size={28} />
            <PopTownName size={20} />
          </>
        )}
      </button>
      {/* il tag della versione apre changelog e controllo degli aggiornamenti */}
      {bp.sm && (
        <button
          onClick={() => setVersionOpen(true)}
          className="t-mono-xs dim"
          title="Novità e aggiornamenti"
          style={{
            border: '1px solid var(--border)',
            background: 'var(--bg-2)',
            borderRadius: 'var(--r-full)',
            padding: '2px 8px',
            cursor: 'pointer',
          }}
        >
          v{__APP_VERSION__}
        </button>
      )}
      {versionOpen && <VersionModal onClose={() => setVersionOpen(false)} />}

      <span style={{ flex: 1 }} />

      {waiting > 0 && (
        <button
          aria-live="polite"
          onClick={() => nav('/')}
          className="anim-tick"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 28,
            padding: '0 12px',
            border: 'none',
            borderRadius: 'var(--r-full)',
            background: 'var(--warn-soft)',
            color: 'var(--warn)',
            font: '500 14px/20px var(--font-ui)',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <Icon name="hand" size={16} />
          <span className="t-num" style={{ color: 'inherit' }}>
            {waiting}
          </span>
          {bp.sm ? 'aspettano te' : ''}
        </button>
      )}

      <button
        onClick={reconnect}
        title={
          state.sse === 'live' ? 'connesso a /api/stream' : 'clicca per forzare la riconnessione'
        }
        style={{
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: sse.color,
          font: '500 12px/16px var(--font-ui)',
        }}
      >
        <span
          className={sse.pulse ? 'anim-pulse' : undefined}
          style={{ width: 8, height: 8, borderRadius: '50%', background: 'currentColor' }}
        />
        {bp.lg ? sse.label : ''}
      </button>

      {/* le notifiche non stanno nel menu (tela, artboard 39): la campanella con il numero */}
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <Button
          variant="icon"
          icon="bell"
          aria-label={unread > 0 ? `Notifiche, ${unread} da leggere` : 'Notifiche'}
          onClick={() => nav('/notifiche')}
        />
        {unread > 0 && (
          <span aria-hidden className="nav-badge header-badge">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </span>

      <div ref={themeBox} style={{ position: 'relative' }}>
        <Button
          variant="icon"
          icon={choice === 'system' ? 'monitor' : choice === 'dark' ? 'moon' : 'sun'}
          aria-label="Tema"
          aria-haspopup="menu"
          aria-expanded={themeMenu}
          onClick={() => setThemeMenu(!themeMenu)}
        />
        {themeMenu && (
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 36,
              zIndex: 200,
              background: 'var(--panel)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)',
              boxShadow: 'var(--shadow-1)',
              padding: 4,
              minWidth: 200,
            }}
          >
            {(['light', 'dark', 'system'] as ThemeChoice[]).map((c) => (
              <button
                key={c}
                onClick={() => setTheme(c)}
                style={{
                  width: '100%',
                  height: 32,
                  padding: '0 10px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: 'var(--text)',
                  font: '400 14px/20px var(--font-ui)',
                }}
              >
                <Icon name={c === 'light' ? 'sun' : c === 'dark' ? 'moon' : 'monitor'} size={16} />
                <span style={{ flex: 1, textAlign: 'left' }}>
                  {c === 'light' ? 'Chiaro' : c === 'dark' ? 'Scuro' : 'Sistema'}
                </span>
                {choice === c && <Icon name="check" size={14} color="var(--accent)" />}
              </button>
            ))}
            <div
              className="t-overline faint"
              style={{
                padding: '10px 10px 6px',
                borderTop: '1px solid var(--border)',
                marginTop: 4,
              }}
            >
              Dimensione
            </div>
            <div
              role="group"
              aria-label="Dimensione dell'interfaccia"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 4,
                padding: '0 4px 4px',
              }}
            >
              {SCALES.map((v) => (
                <button
                  key={v}
                  aria-pressed={scale === v}
                  onClick={() => {
                    applyScale(v);
                    setScale(v);
                  }}
                  style={{
                    height: 28,
                    border: `1px solid ${scale === v ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 'var(--r-sm)',
                    background: scale === v ? 'var(--accent-soft)' : 'none',
                    color: scale === v ? 'var(--accent)' : 'var(--text)',
                    cursor: 'pointer',
                    font: '500 12px/16px var(--font-mono)',
                  }}
                >
                  {Math.round(v * 100)}%
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
